import React, { useEffect, useState } from 'react';
import { FieldProps } from 'formik';
import moment from 'moment';
import MaskedInput, { DATE_MASK, DATE_FORMAT } from '../../../CommonComponents/MaskedInput';
import {
  StyledActivityDateTimePicker,
  ActivityDateTimePickerColumn,
  ActivityDateTimePickerCurrentDateTime,
  AddUserActivityModalTimePicker,
  ActivityDateTimePickerGroupButton,
} from './ActivityDateTimePickerStyles';
import { AddUserActivityLabel } from '../AddUserActivity/AddUserActivityStyles';
import Select from '../../../CommonComponents/Select';
import { HighlightedLinkButton } from '../../../CommonComponents/Button';
import { buildHoursOptions, buildMinutesOptions } from './ActivityDateTimePickerConstants';

const hoursOptions = buildHoursOptions();
const minutesOptions = buildMinutesOptions();
const getNowHours = () => parseInt(moment().format('h'), 10);
const getNowMinutes = () => moment().minutes();
const getNowAmPm = () => moment().format('A') as 'AM' | 'PM';
const hoursTo24Format = (hours, amPm) => {
  if (hours === 12) {
    return amPm === 'AM' ? 0 : 12;
  }
  return amPm === 'AM' ? hours : hours + 12;
};

interface Props extends FieldProps {}

export const ActivityDateTimePicker: React.FC<Props> = ({
  field: { name },
  form: { setFieldValue },
  ...props
}) => {
  const [date, setDate] = useState(moment().format(DATE_FORMAT));
  const [hours, setHours] = useState<number>(getNowHours());
  const [minutes, setMinutes] = useState<number>(getNowMinutes());
  const [amPm, setAmPm] = useState<'AM' | 'PM'>(getNowAmPm());

  useEffect(() => {
    if (date) {
      const parsedDate = moment(date, DATE_FORMAT)
        .hours(hoursTo24Format(hours, amPm))
        .minutes(minutes);

      if (parsedDate.isValid()) {
        setFieldValue(name, parsedDate.utc().toISOString());
      } else {
        setFieldValue(name, '');
      }
    } else {
      setFieldValue(name, '');
    }
  }, [date, hours, minutes, amPm, name, setFieldValue]);

  return (
    <StyledActivityDateTimePicker {...props}>
      <ActivityDateTimePickerColumn>
        <AddUserActivityLabel>Date</AddUserActivityLabel>
        <MaskedInput
          value={date}
          onChange={({ target: { value } }) => {
            setDate(value);
          }}
          mask={DATE_MASK}
          placeholder="MM-DD-YYYY"
        />
        <ActivityDateTimePickerCurrentDateTime>
          <HighlightedLinkButton onClick={() => setDate(moment().format(DATE_FORMAT))}>
            Current Date
          </HighlightedLinkButton>
        </ActivityDateTimePickerCurrentDateTime>
      </ActivityDateTimePickerColumn>
      <ActivityDateTimePickerColumn>
        <AddUserActivityLabel>Time</AddUserActivityLabel>
        <AddUserActivityModalTimePicker>
          <Select
            value={hoursOptions.find(option => option.value === hours)}
            placeholder="00"
            options={hoursOptions}
            onChange={opt => setHours(opt.value)}
          />
          <Select
            value={minutesOptions.find(option => option.value === minutes)}
            placeholder="00"
            options={minutesOptions}
            onChange={opt => setMinutes(opt.value)}
          />
          <ActivityDateTimePickerGroupButton amPm={amPm}>
            <button type="button" onClick={() => setAmPm('AM')}>
              AM
            </button>
            <button type="button" onClick={() => setAmPm('PM')}>
              PM
            </button>
          </ActivityDateTimePickerGroupButton>
        </AddUserActivityModalTimePicker>
        <ActivityDateTimePickerCurrentDateTime>
          <HighlightedLinkButton
            onClick={() => {
              setHours(getNowHours());
              setMinutes(getNowMinutes());
              setAmPm(getNowAmPm());
            }}
          >
            Current Time
          </HighlightedLinkButton>
        </ActivityDateTimePickerCurrentDateTime>
      </ActivityDateTimePickerColumn>
    </StyledActivityDateTimePicker>
  );
};
